/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';

export const namespaced = true;

export const state = {
  loading: false,
  photoLoading: false,
  rightsLoading: false,
  currentProject: null,
  projectPhoto: null,
  serverError: null,
  uploading: false,
  submitting: false,
  developmentRightsSummary: null,
  searchResults: [],
};

export const actions = {

  async fetch({ commit, dispatch, state }, { projectId }) {
    // only fetch if not already loaded
    if (!state.currentProject || state.currentProject.id !== projectId) {
      commit('SET_LOADING', true);
      await api.get(`/project/get/${projectId}`)
        .then((response) => {
          commit('SET_CURRENT_PROJECT', response.data);
          commit('SET_SERVER_ERROR', '');
          commit('SET_LOADING', false);
          dispatch('CurrentProject/fetchProjectDeleteMessage', { projectId }, { root: true });
        })
        .catch((e) => {
          commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
          commit('SET_LOADING', false);
        });
    }
  },
  async refreshLatestEvents({ commit, state }, { projectId }) {
    if (state.currentProject && state.currentProject.id === projectId) {
      await api.get(`project/${projectId}/refreshLatestEvents`)
        .then((response) => {
          commit('SET_CURRENT_PROJECT_LATEST_EVENTS', response.data); 
        })
        .catch((e) => {
          commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
        });
    }
  },

  async fetchDevelopmentRightsSummary({ commit, state }, { projectId, forceFetch }) {
    // only fetch if not already loaded
    if (!state.developmentRightsSummary
        || state.developmentRightsSummary.projectId !== projectId
        || forceFetch) {
      commit('SET_RIGHTS_LOADING', true);
      await api.get(`/project/${projectId}/developmentRightsSummary`)
        .then((response) => {
          commit('SET_DEVELOPMENT_RIGHTS_SUMMARY', response.data);
          commit('SET_SERVER_ERROR', '');
          commit('SET_RIGHTS_LOADING', false);
        })
        .catch((e) => {
          commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
          commit('SET_RIGHTS_LOADING', false);
        });
    }
  },
  async updateDocumentFolder({ commit }, { id, folderId }) {
    commit('START_SUBMITTING');
    await api.patch(`/project/${id}/updateDocumentFolder`, { folderId })
      .then((response) => {
        commit('SET_DOCUMENT_FOLDER', response.data);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  fetchProjectDeleteMessage({ commit }, { projectId }) {
    commit('SET_DELETE_MESSAGE_LOADING', true);
    api.get(`/project/${projectId}/delete_message`)
      .then((response) => {
        commit('SET_PROJECT_DELETE_MESSAGE', `${response.data}`);
        commit('SET_DELETE_MESSAGE_LOADING', false);
        commit('SET_SERVER_ERROR', '');
        return response.data;
      })
      .catch((e) => {
        commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
        commit('SET_DELETE_MESSAGE_LOADING', false);
      });
  },
  async deleteProject({ commit }, { projectId, submittedCallback }) {
    commit('START_SUBMITTING');
    await api.delete(`/project/delete/${projectId}`)
      .then(() => {
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        submittedCallback();
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async fetchPhoto({ commit }, { projectId }) {
    commit('SET_PHOTO_LOADING', true);
    await api.get(`/project/${projectId}/photo`)
      .then((response) => {
        if (response.data !== '') {
          commit('SET_PROJECT_PHOTO', `data:image/png;base64, ${response.data}`);
        }
        commit('SET_PHOTO_LOADING', false);
        commit('SET_SERVER_ERROR', '');
      })
      .catch((e) => {
        commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
        commit('SET_PHOTO_LOADING', false);
      });
  },
  clearCurrentProject({ commit }) {
    commit('SET_CURRENT_PROJECT', null);
  },
  clearCurrentPhoto({ commit }) {
    commit('SET_PROJECT_PHOTO', '');
  },
  async updateEntity({ commit }, { project, submittedCallback }) {
    commit('SET_SUBMITTING', true);
    api.post(`/project/${project.id}/update`, project)
      .then(() => {
        commit('SET_SERVER_ERROR', '');
        if (submittedCallback) {
          submittedCallback();
        }
      }).catch((e) => {
        commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
      }).finally(() => {
        commit('SET_SUBMITTING', false);
      });
  },
  async uploadPhoto({ commit, dispatch }, { photoSummary, projectId, submittedCallback }) {
    commit('SET_UPLOADING', true);
    const formData = new FormData();
    formData.append('formFile', photoSummary);
    api.post(
      `/project/${projectId}/uploadphoto`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(() => {
      dispatch('CurrentProject/clearCurrentProject', null, { root: true });
      dispatch('CurrentProject/fetch',
        { projectId },
        { root: true });
      dispatch('CurrentProject/clearCurrentPhoto', null, { root: true });
      dispatch('CurrentProject/fetchPhoto', { projectId }, { root: true });
      if (submittedCallback) {
        submittedCallback();
      }
    }).catch((e) => {
      commit('SET_SERVER_ERROR', (e.response && e.response.data) || e);
    }).finally(() => {
      commit('SET_UPLOADING', false);
    });
  },
  async search({ commit }) {
    await api.get('/project/search')
      .then((response) => {
        commit('SET_SEARCH_RESULTS', response.data);
      });
  },
};

export const mutations = {
  SET_CURRENT_PROJECT(state, currentProject) {
    state.currentProject = currentProject;
  },
  SET_DEVELOPMENT_RIGHTS_SUMMARY(state, developmentRightsSummary) {
    state.developmentRightsSummary = developmentRightsSummary;
  },
  SET_PROJECT_PHOTO(state, projectPhoto) {
    state.projectPhoto = projectPhoto;
  },
  SET_PROJECT_DELETE_MESSAGE(state, projectDeleteMessage) {
    state.projectDeleteMessage = projectDeleteMessage;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_RIGHTS_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_PHOTO_LOADING(state, photoLoading) {
    state.photoLoading = photoLoading;
  },
  SET_DELETE_MESSAGE_LOADING(state, deleteMessageLoading) {
    state.deleteMessageLoading = deleteMessageLoading;
  },
  SET_CURRENT_PROJECT_LATEST_EVENTS(state, latestEvents) {
    state.currentProject.latestEvents = latestEvents;
  },
  SET_UPLOADING(state, uploading) {
    state.uploading = uploading;
  },
  SET_SUBMITTING(state, submitting) {
    state.submitting = submitting;
  },
  SET_SERVER_ERROR(state, error) {
    state.error = error;
  },
  SET_SEARCH_RESULTS(state, results) {
    state.searchResults = results;
  },
  SET_DOCUMENT_FOLDER(state, folderId) {
    state.currentProject.documentFolderId = folderId;
  },
  START_SUBMITTING(state) {
    state.submitting = true;
  },
  STOP_SUBMITTING(state) {
    state.submitting = false;
  },
};

export function entitySort(a, b) {
  const aDate = new Date(a.date);
  const bDate = new Date(b.date);
  return (bDate.getYear() - aDate.getYear())
    || (bDate.getMonth() - aDate.getMonth())
    || (bDate.getDate() - aDate.getDate())
    || (b.id - a.id);
}

export const getters = {
  projectLocalities: (state) => {
    const localities = [];
    if (state.currentProject) {
      state.currentProject.properties.forEach((property) => {
        if (property.statusId === 1) {
          property.propertyLocalities.forEach((propertyLocality) => {
            localities.push(propertyLocality.locality.name);
          });
        }
      });
    }
    // Set() used to remove duplicates
    return Array.from(new Set(localities)).sort();
  },
  latestEvents: (state) => {
    if (state.currentProject && state.currentProject.latestEvents) {
      return state.currentProject.latestEvents.slice(0, 5)
        .sort((a, b) => (entitySort(a, b)));
    }
    return [];
  },
};
